/**
 * FAQPage.jsx — Full Workshop Details & FAQ page
 *
 * Accordion-style list of all FAQ items, rendered as a full-page view.
 * Navigated to when the user clicks the FAQ tile's "Browse FAQ" button.
 *
 * Props:
 *   onReset {Function} — returns to the browse homepage
 */
import { useState } from 'react';
import { useFAQContent } from '../services/firestore';

export default function FAQPage({ onReset }) {
  const { faq } = useFAQContent();
  const { page, items } = faq;

  const [openIdx, setOpenIdx] = useState(null);
  const toggle = (i) => setOpenIdx(openIdx === i ? null : i);

  return (
    <div
      className="fade section-pad"
      style={{ paddingTop: 32, paddingBottom: 48 }}
    >
      {/* ── Header tile ──────────────────────────────────────────────────── */}
      <div
        className="tile"
        style={{
          padding: '40px 48px 32px',
          marginBottom: 24,
          position: 'relative',
        }}
      >
        {/* Corner ornaments */}
        <div
          style={{
            position: 'absolute',
            top: 14,
            left: 20,
            color: '#ccc',
            fontSize: '0.65rem',
            letterSpacing: 4,
          }}
        >
          ◦ ◦
        </div>
        <div
          style={{
            position: 'absolute',
            top: 14,
            right: 20,
            color: '#ccc',
            fontSize: '0.65rem',
            letterSpacing: 4,
          }}
        >
          ◦ ◦
        </div>

        {/* Back navigation */}
        <button
          onClick={onReset}
          style={{
            fontFamily: "'Raleway', sans-serif",
            fontWeight: 700,
            fontSize: '0.75rem',
            letterSpacing: '0.12em',
            textTransform: 'uppercase',
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            color: '#9C7A3C',
            marginBottom: 20,
            display: 'block',
          }}
        >
          ← Back
        </button>

        <p
          style={{
            fontFamily: "'Raleway', sans-serif",
            fontWeight: 700,
            fontSize: '0.7rem',
            letterSpacing: '0.18em',
            textTransform: 'uppercase',
            color: '#9C7A3C',
            marginBottom: 10,
          }}
        >
          {page.label}
        </p>
        <h2
          style={{
            fontFamily: "'Titan One', sans-serif",
            fontWeight: 400,
            fontSize: 'clamp(2rem, 5vw, 3rem)',
            lineHeight: 0.95,
            letterSpacing: '-0.01em',
            color: '#1a1208',
            marginBottom: 16,
          }}
        >
          {page.heading}
        </h2>
        <p
          style={{
            fontFamily: "'Raleway', sans-serif",
            fontWeight: 700,
            fontSize: 'clamp(1rem, 1.8vw, 1.1rem)',
            color: '#1a1208',
            lineHeight: 1.6,
            maxWidth: 540,
          }}
        >
          {page.description}
        </p>
      </div>

      {/* ── Accordion ────────────────────────────────────────────────────── */}
      <div className="tile" style={{ overflow: 'hidden' }}>
        {items.map((item, i) => {
          const isOpen = openIdx === i;
          return (
            <div
              key={i}
              style={{
                borderBottom:
                  i < items.length - 1 ? '1px solid #DDD8CC' : 'none',
              }}
            >
              {/* Question row */}
              <button
                onClick={() => toggle(i)}
                aria-expanded={isOpen}
                style={{
                  width: '100%',
                  background: 'none',
                  border: 'none',
                  padding: '20px 32px',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  cursor: 'pointer',
                  textAlign: 'left',
                  gap: 16,
                }}
              >
                <span
                  style={{
                    fontFamily: "'Raleway', sans-serif",
                    fontSize: '0.9rem',
                    color: isOpen ? '#9C7A3C' : '#1a1208',
                    transition: 'color 0.2s',
                  }}
                >
                  {item.q}
                </span>
                <span
                  style={{
                    color: '#9C7A3C',
                    fontSize: '1.1rem',
                    lineHeight: 1,
                    flexShrink: 0,
                    transition: 'transform 0.2s',
                    transform: isOpen ? 'rotate(45deg)' : 'none',
                  }}
                >
                  +
                </span>
              </button>

              {/* Answer panel */}
              <div
                style={{
                  maxHeight: isOpen ? '600px' : 0,
                  overflow: 'hidden',
                  transition: 'max-height 0.3s ease',
                }}
              >
                <div
                  style={{
                    padding: '0 32px 24px',
                    fontFamily: "'Raleway', sans-serif",
                    fontWeight: 700,
                    fontSize: '0.9rem',
                    color: '#1a1208',
                    lineHeight: 1.8,
                  }}
                >
                  <p style={{ margin: '0 0 10px' }}>{item.a}</p>
                  {item.bullets && (
                    <ul
                      style={{
                        margin: '0 0 10px',
                        paddingLeft: 20,
                        listStyle: 'disc',
                      }}
                    >
                      {item.bullets.map((b, j) => (
                        <li key={j} style={{ marginBottom: 4 }}>
                          {b}
                        </li>
                      ))}
                    </ul>
                  )}
                  {item.note && (
                    <p
                      style={{
                        margin: 0,
                        fontStyle: 'italic',
                        fontSize: '0.78rem',
                        color: '#aaa',
                      }}
                    >
                      {item.note}
                    </p>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
